import React, { useContext, useState, useMemo, useEffect } from "react";
import {
  ITravelOfferCardContent,
  TravelWalletDetailsBanner,
  ITravelWalletDetailsBannerVariant,
  getTravelOfferCardContent,
  TravelCreditDetailsCardContent,
  getTravelCreditCardContent,
  Icon,
  IconName,
  getTravelCreditHistoryModalProps,
  useDeviceTypes,
  isOfferHSPTiered,
  getWalletBannerCreditToShow,
  isSpendAndGetOffer,
} from "halifax";

import {
  TravelWalletOffer,
  SELECTED_TRAVEL_OFFER,
  VIEWED_OFFER_FAQ,
  SelectedTravelOfferProperties,
  VIEWED_CREDIT_BANNER,
  ENGAGED_CREDIT_CTA,
  VIEW_CREDIT_HISTORY,
  VIEWED_OFFER_BANNER,
  ENGAGED_OFFER_CTA,
  RestrictedToProductsEnum,
} from "redmond";
import { RouteComponentProps } from "react-router";
import {
  CREDITS_MODAL_HEADER_TEXT,
  getTravelCreditBannerText,
  getTravelOfferBannerText,
  VIEW_DETAILS_CTA_COPY,
  VIEW_OFFER_DETAILS_CTA_COPY,
} from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { TravelWalletDetailsBannerConnectorProps } from "./container";
import { ClientContext } from "../../../../App";
import "./styles.scss";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CONTROL,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";
import { HSP_OFFER_TITLE_LABEL } from "../TravelWalletDrawer/constants";
import { config } from "../../../../api/config";

interface IFlightTravelWalletDetailsBannerProps
  extends TravelWalletDetailsBannerConnectorProps,
    RouteComponentProps {
  offer?: TravelWalletOffer;
  showButton?: boolean;
  className?: string;
  variant?: ITravelWalletDetailsBannerVariant;
  screen?: SelectedTravelOfferProperties["screen"];
  onDismiss?: () => void;
}

export const FlightTravelWalletDetailsBanner = (
  props: IFlightTravelWalletDetailsBannerProps
) => {
  const {
    offer,
    showButton,
    className,
    variant,
    screen,
    credit,
    creditBreakdown,
    creditsTransactionHistory,
    rewardsAccounts,
    onDismiss,
  } = props;

  const { matchesMobile } = useDeviceTypes();

  const [bestOffer, setBestOffer] = useState(offer);
  const [openModal, setOpenModal] = useState(false);
  const [
    travelWalletDetailsBannerModalContent,
    setTravelWalletDetailsBannerModalContent,
  ] = useState<ITravelOfferCardContent | TravelCreditDetailsCardContent | null>(
    null
  );
  const [travelWalletDetailsBannerText, setTravelWalletDetailsBannerText] =
    useState("");
  const [isCredit, setIsCredit] = useState(false);

  const firstName =
    useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const expState = useExperiments();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const creditDetailToShow = getWalletBannerCreditToShow(
    credit?.breakdown || creditBreakdown,
    rewardsAccounts,
    RestrictedToProductsEnum.Air
  );

  useEffect(() => {
    if (offer && offer != bestOffer) {
      setBestOffer(offer);
    }
    if (credit && isTravelWalletCreditsExperiment) {
      // bestOffer as a prop comes from /shopSummary as the best bestOffer so that amount is the same as the credit amount, then we know the best bestOffer we get back is credit (so we display credit text)
      const bestOfferIsACredit = !!(
        !bestOffer ||
        Math.abs(credit.amount.amount) === Math.abs(bestOffer.amount.amount) ||
        !!creditDetailToShow
      );
      setIsCredit(bestOfferIsACredit);
      if (bestOfferIsACredit) {
        trackEvent({
          eventName: VIEWED_CREDIT_BANNER,
          properties: {},
        });
      } else {
        trackEvent({
          eventName: VIEWED_OFFER_BANNER,
          properties: {
            location: screen,
          },
          encryptedProperties: bestOffer
            ? [bestOffer.trackingPropertiesV2?.encryptedProperties ?? ""]
            : [],
        });
      }
    } else {
      setIsCredit(false);
    }
  }, [offer, credit]);

  useEffect(() => {
    const content =
      isCredit && credit
        ? getTravelCreditCardContent(
            creditBreakdown,
            config.TENANT,
            firstName,
            credit.amount,
            isCreditAndOfferStackingExperimentV1
          )
        : bestOffer
        ? getTravelOfferCardContent(
            bestOffer,
            isCreditAndOfferStackingExperimentV1
          )
        : undefined;
    setTravelWalletDetailsBannerModalContent(content);
    const text =
      isCredit && credit
        ? getTravelCreditBannerText(credit, creditDetailToShow)
        : bestOffer
        ? getTravelOfferBannerText(bestOffer)
        : undefined;

    text && setTravelWalletDetailsBannerText(text);
  }, [isCredit, creditDetailToShow]);

  const isOfferSpendAndGet = offer && isSpendAndGetOffer(offer.descriptions);

  if (
    (!bestOffer && !credit && !creditDetailToShow) ||
    !travelWalletDetailsBannerText ||
    isOfferSpendAndGet
  )
    return null;

  return isCredit ? (
    <TravelWalletDetailsBanner
      className={className}
      openModal={openModal}
      onDismissClick={onDismiss}
      setOpenModal={(open) => {
        setOpenModal(open);
        if (open) {
          trackEvent({
            eventName: ENGAGED_CREDIT_CTA,
            properties: {},
          });
        }
      }}
      travelOfferBannerText={travelWalletDetailsBannerText}
      modalHeaderText={CREDITS_MODAL_HEADER_TEXT(firstName)}
      modalCTACopy={VIEW_DETAILS_CTA_COPY}
      showButton={showButton}
      onFirstTravelOfferCardChange={() => {
        trackEvent({
          eventName: VIEWED_OFFER_FAQ,
          properties: {
            screen,
          },
        });
      }}
      creditDetailsCardContent={travelWalletDetailsBannerModalContent}
      variant={variant}
      icon={<Icon name={IconName.PiggyBank} />}
      modalVariant="credit"
      showCreditHistory={
        isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
      }
      travelCreditHistoryProps={
        isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
          ? {
              ...getTravelCreditHistoryModalProps(
                firstName,
                creditsTransactionHistory,
                credit,
                !matchesMobile ? IconName.PiggyBankCircleOutline : undefined
              ),
              rewardsAccounts,
              credit,
              isAnnualTravelCreditsExperiment,
            }
          : undefined
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      onViewCreditHistory={() =>
        trackEvent({
          eventName: VIEW_CREDIT_HISTORY,
          properties: {},
        })
      }
    />
  ) : (
    <TravelWalletDetailsBanner
      className={className}
      openModal={openModal}
      onDismissClick={onDismiss}
      setOpenModal={(open) => {
        setOpenModal(open);
        if (open && bestOffer) {
          trackEvent({
            eventName: SELECTED_TRAVEL_OFFER,
            properties: {
              screen,
              ...bestOffer.trackingPropertiesV2,
            },
          });

          trackEvent({
            eventName: ENGAGED_OFFER_CTA,
            properties: {
              location: screen,
              entry_type: "banner",
              funnel: bestOffer.funnels.join(","),
            },
            encryptedProperties: [
              bestOffer.trackingPropertiesV2?.encryptedProperties ?? "",
            ],
          });
        }
      }}
      travelOfferCardContent={travelWalletDetailsBannerModalContent}
      travelOfferBannerText={travelWalletDetailsBannerText}
      modalCTACopy={VIEW_OFFER_DETAILS_CTA_COPY}
      showButton={showButton}
      variant={variant}
      onFirstTravelOfferCardChange={() => {
        trackEvent({
          eventName: VIEWED_OFFER_FAQ,
          properties: {
            screen,
          },
        });
      }}
      offerFunnels={offer?.funnels}
      offerLocation={offer?.descriptions[2]}
      isTravelSale={isTravelSaleEnabled}
      imageId={offer?.imageId}
      imageFolderUrl={offer?.imageFolderUrl}
      modalTitleLabelText={
        offer && isOfferHSPTiered(offer.descriptions)
          ? HSP_OFFER_TITLE_LABEL
          : undefined
      }
    />
  );
};
